import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useCallback, useEffect, useState } from 'react';

import { STORAGE_KEYS } from '@/constants/storageKeys';
import { setNativeAnalyticsDisabled } from '@/util/analyticsState';
import { getPlatformStorage } from '@/util/platformStorage';

type AnalyticsSettings = {
  isSegmentEnabled: boolean;
};

/**
 * Retrieves initial analytics settings from storage.
 *
 * For native platforms, also updates the native analytics disabled state
 * to ensure consistency between storage and native analytics configuration.
 */
async function getInitialSettings(): Promise<AnalyticsSettings> {
  const storage = getPlatformStorage(undefined, 'local');

  const segmentEnabled = await storage.getItem(STORAGE_KEYS.ANALYTICS_SEGMENT_ENABLED);

  const isSegmentEnabled = segmentEnabled === null ? true : segmentEnabled === 'true';

  if (!isWeb) {
    setNativeAnalyticsDisabled(!isSegmentEnabled);
  }

  return { isSegmentEnabled };
}

export function useAnalyticsSettings() {
  const [settings, setSettings] = useState<AnalyticsSettings | undefined>();

  useEffect(() => {
    getInitialSettings()
      .then(setSettings)
      .catch(() => {
        setSettings({ isSegmentEnabled: true });
      });
  }, []);

  /**
   * Updates analytics settings in storage and state.
   *
   * For native platforms, also updates the native analytics disabled state
   * to ensure consistency between storage and native analytics configuration.
   */
  const setSegmentEnabled = useCallback(async (enabled: boolean) => {
    const storage = getPlatformStorage(undefined, 'local');

    await storage.setItem(STORAGE_KEYS.ANALYTICS_SEGMENT_ENABLED, String(enabled));

    if (!isWeb) {
      setNativeAnalyticsDisabled(!enabled);
    }

    setSettings((prev) => ({
      ...(prev ?? { isSegmentEnabled: true }),
      isSegmentEnabled: enabled,
    }));
  }, []);

  return { settings, setSegmentEnabled };
}
